import AWS from 'aws-sdk';
import { config } from '../config';

export interface AWSConfig {
  region: string;
  s3Bucket: string;
  identityPoolId?: string;
  userPoolId?: string;
  clientId?: string;
  hostedUIUrl?: string;
}

export const awsConfig: AWSConfig = {
  region: config.aws.region,
  s3Bucket: config.aws.s3Bucket,
  identityPoolId: config.aws.identityPoolId,
  userPoolId: config.aws.userPoolId,
  clientId: config.aws.clientId,
  hostedUIUrl: config.aws.hostedUIUrl,
};

/**
 * Initialize AWS SDK with Identity Pool credentials
 */
export const initializeAWS = async (): Promise<AWS.S3> => {
  // Only initialize credentials if they don't already exist
  // (auth service sets up credentials with JWT tokens)
  if (!AWS.config.credentials) {
    if (awsConfig.identityPoolId) {
      // Use Identity Pool credentials as fallback
      AWS.config.update({
        region: awsConfig.region,
        credentials: new AWS.CognitoIdentityCredentials({
          IdentityPoolId: awsConfig.identityPoolId,
        }),
      });
    } else {
      throw new Error('Identity Pool ID is required for authentication. Please configure REACT_APP_AWS_IDENTITY_POOL_ID in your environment variables.');
    }
  } else {
    // Just ensure region is set if credentials already exist
    AWS.config.region = awsConfig.region;
  }

  // Ensure credentials are refreshed before returning S3 instance
  if (AWS.config.credentials) {
    return new Promise((resolve, reject) => {
      (AWS.config.credentials as any).refresh((error: any) => {
        if (error) {
          console.error('Failed to refresh credentials:', error);
          reject(error);
        } else {
          resolve(new AWS.S3());
        }
      });
    });
  }

  return new AWS.S3();
};

/**
 * Upload a file to S3 with proper naming conventions
 */
export const uploadToS3 = async (
  file: File,
  folder: string = 'uploads',
  s3Instance?: AWS.S3
): Promise<string> => {
  const s3 = s3Instance || await initializeAWS();
  const fileName = generateFileName(file.name);
  const params: AWS.S3.PutObjectRequest = {
    Bucket: awsConfig.s3Bucket,
    Key: `${folder}/${fileName}`,
    Body: file,
    ContentType: file.type,
    ACL: 'public-read',
  };

  try {
    const result = await s3.upload(params).promise();
    return result.Location;
  } catch (error) {
    console.error('Error uploading to S3:', error);
    throw error;
  }
};

/**
 * Generate a unique filename with timestamp
 */
export const generateFileName = (originalName: string): string => {
  const timestamp = Date.now();
  const extension = originalName.split('.').pop();
  const nameWithoutExtension = originalName.replace(/\.[^/.]+$/, "");
  const sanitizedName = nameWithoutExtension.replace(/[^a-zA-Z0-9]/g, '_');
  return `${sanitizedName}_${timestamp}.${extension}`;
};

/**
 * Generate a signed URL for private S3 objects
 */
export const getSignedUrl = async (
  s3Key: string,
  bucket?: string,
  expiresIn: number = 3600
): Promise<string> => {
  const s3 = await initializeAWS();
  
  const params = {
    Bucket: bucket || awsConfig.s3Bucket,
    Key: s3Key,
    Expires: expiresIn,
  };

  return s3.getSignedUrl('getObject', params);
};

/**
 * Extract bucket and key from S3 URL
 */
export const parseS3Url = (s3Url: string): { bucket: string; key: string } => {
  // Handle: https://bucket.s3.region.amazonaws.com/key
  const match = s3Url.match(/https:\/\/([^.]+)\.s3\.[^.]+\.amazonaws\.com\/(.+)/);
  if (match) {
    return { bucket: match[1], key: match[2] };
  }
  
  // Handle: s3://bucket/key
  if (s3Url.startsWith('s3://')) {
    const parts = s3Url.replace('s3://', '').split('/');
    return { bucket: parts[0], key: parts.slice(1).join('/') };
  }
  
  // Fallback - assume it's just a key
  return { bucket: awsConfig.s3Bucket, key: s3Url };
};
